import React, { useState, useEffect, useRef } from 'react';
import { db, auth, getCollectionName } from './firebase.js';
import { collection, onSnapshot, query, orderBy, addDoc, serverTimestamp } from './firestore-compat';

export default function ComentariosChat({ isOpen, onClose, ocorrenciaId, tarefaId, tarefaTitulo }) {
  const [mensagens, setMensagens] = useState([]);
  const [novoComentario, setNovoComentario] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !ocorrenciaId || !tarefaId) return;

    const comentariosRef = collection(db, 'ocorrencias', tarefaId, 'comentarios');
    const q = query(comentariosRef, orderBy('timestamp', 'asc'));

    const unsub = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setMensagens(data);
      // Auto-scroll to bottom
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    });

    return () => unsub();
  }, [isOpen, ocorrenciaId, tarefaId]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!novoComentario.trim() || !auth.currentUser) return;

    setLoading(true);
    try {
      const comentariosRef = collection(db, 'ocorrencias', tarefaId, 'comentarios');
      await addDoc(comentariosRef, {
        texto: novoComentario.trim(),
        autorNome: auth.currentUser.displayName || auth.currentUser.email || 'Usuário',
        autorUid: auth.currentUser.uid,
        timestamp: serverTimestamp(),
        tipo: 'mensagem'
      });
      setNovoComentario('');
    } catch (err) {
      console.error("Erro ao enviar mensagem:", err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
      `}</style>
      
      {/* OVERLAY */}
      <div 
        style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}
        onClick={onClose}
      />
      
      {/* CHAT PANEL */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: '400px', backgroundColor: '#f7fafc',
        boxShadow: '-5px 0 25px rgba(0,0,0,0.15)', zIndex: 10000, display: 'flex', flexDirection: 'column',
        animation: 'slideInRight 0.3s ease'
      }}>
        
        {/* HEADER */}
        <div style={{ padding: '20px', backgroundColor: '#fff', borderBottom: '1px solid #edf2f7', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#1a1a2e' }}>Chat / Comentários</h3>
            <div style={{ fontSize: '0.85rem', color: '#718096', marginTop: '4px' }}>Tarefa: {tarefaTitulo}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#a0aec0' }}>&times;</button>
        </div>

        {/* MESSAGES */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
          {mensagens.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#a0aec0', fontSize: '0.9rem', marginTop: '20px' }}>Nenhum comentário ainda.</div>
          ) : (
            mensagens.map(msg => {
              const isMine = msg.autorUid === auth.currentUser?.uid;
              const dateObj = msg.timestamp?.toDate ? msg.timestamp.toDate() : new Date();
              const timeStr = dateObj.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
              
              if (msg.tipo === 'sistema') {
                return (
                  <div key={msg.id} style={{ textAlign: 'center', margin: '10px 0' }}>
                    <span style={{ backgroundColor: '#edf2f7', color: '#718096', fontSize: '0.75rem', padding: '4px 12px', borderRadius: '12px' }}>
                      {msg.texto} ({timeStr})
                    </span>
                  </div>
                );
              }
              
              return (
                <div key={msg.id} style={{ display: 'flex', flexDirection: 'column', alignItems: isMine ? 'flex-end' : 'flex-start' }}>
                  <div style={{ fontSize: '0.75rem', color: '#718096', marginBottom: '4px', padding: '0 4px' }}>{isMine ? 'Você' : msg.autorNome}</div>
                  <div style={{ 
                    backgroundColor: isMine ? '#667eea' : '#fff', 
                    color: isMine ? '#fff' : '#4a5568',
                    padding: '10px 14px',
                    borderRadius: '12px',
                    borderTopRightRadius: isMine ? '0' : '12px',
                    borderTopLeftRadius: !isMine ? '0' : '12px',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
                    maxWidth: '85%',
                    fontSize: '0.9rem',
                    lineHeight: '1.4'
                  }}>
                    {msg.texto}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#a0aec0', marginTop: '4px', padding: '0 4px' }}>{timeStr}</div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* INPUT */}
        <div style={{ padding: '20px', backgroundColor: '#fff', borderTop: '1px solid #edf2f7' }}>
          <form onSubmit={handleSend} style={{ display: 'flex', gap: '10px' }}>
            <input 
              type="text" 
              value={novoComentario}
              onChange={e => setNovoComentario(e.target.value)}
              placeholder="Digite um comentário..."
              style={{ flex: 1, padding: '12px 15px', borderRadius: '24px', border: '1px solid #e2e8f0', outline: 'none', backgroundColor: '#f7fafc', fontFamily: "'Inter', sans-serif" }}
            />
            <button 
              type="submit"
              disabled={loading || !novoComentario.trim()}
              style={{ backgroundColor: '#667eea', color: '#fff', border: 'none', width: '45px', height: '45px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: (loading || !novoComentario.trim()) ? 0.6 : 1 }}
            >
              ➤
            </button>
          </form>
        </div>

      </div>
    </>
  );
}
